import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Info, Cable, Server, Router, Network, FileText, Share2 } from 'lucide-react';
import { ShareSection } from '../shared/ShareSection';
import { CsiItemSubmittalsSection } from '../shared/CsiItemSubmittalsSection';
import { CommunicationGeneralInfoSection } from './CommunicationGeneralInfoSection';
import { StructuredCablingSection } from './structured-cabling/StructuredCablingSection';
import { COMMUNICATION_DATA_TYPES } from './communicationConfig';

const TABS = [
  { value: 'comm-general', label: 'General Information', icon: Info },
  { value: 'comm-structured-cabling', label: 'Structured Cabling', icon: Cable },
  { value: 'comm-telecom-rooms', label: 'Telecom Rooms & Racks', icon: Server },
  { value: 'comm-network-equipment', label: 'Network Equipment', icon: Router },
  { value: 'comm-network-topology', label: 'Network Topology', icon: Network },
  { value: 'comm-submittals', label: 'Submittals', icon: FileText },
];

export const CommunicationSystemsSection = ({
  activeSubTab, onSubTabChange,
  projectId, csiItem, canEditCSIItems, canManageProject,
  shareCompanySearch, showCompanySuggestions, companySuggestions,
  highlightedIndex, searchingCompanies,
  handleSearchCompanies, handleCompanySearchKeyDown,
  setShowCompanySuggestions, setHighlightedIndex,
  handleSelectCompany, handleUnshareCSI,
  project, user,
}) => {
  const currentTab = activeSubTab && (TABS.some(t => t.value === activeSubTab) || activeSubTab === 'comm-share')
    ? activeSubTab
    : TABS[0].value;

  return (
    <Tabs value={currentTab} onValueChange={onSubTabChange} className="space-y-6">
      <TabsList className="flex items-center w-full gap-2" data-testid="communication-tabs-list">
        <div className="flex-1 grid grid-cols-6 gap-0">
          {TABS.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              data-testid={`tab-${tab.value}`}
              className="flex items-center gap-1.5 text-xs sm:text-sm"
            >
              <tab.icon className="h-3.5 w-3.5 hidden sm:block" />
              {tab.label}
            </TabsTrigger>
          ))}
        </div>
        {canManageProject && (
          <TabsTrigger value="comm-share" data-testid="tab-comm-share" className="flex-shrink-0">
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </TabsTrigger>
        )}
      </TabsList>

      {/* Sharing Info - below tabs, matching Electrical / Division 8 pattern */}
      {canManageProject && (
        <div className="text-xs text-slate-600 -mt-2" data-testid="comm-share-info">
          Shared with: {csiItem?.assignedCompanyName ? (
            <span className="font-medium text-slate-900">{csiItem.assignedCompanyName}</span>
          ) : (
            <span className="text-slate-400">N/A</span>
          )}
        </div>
      )}

      {/* General Information - full functional section */}
      <TabsContent value="comm-general" data-testid="content-comm-general">
        <CommunicationGeneralInfoSection
          projectId={projectId}
          canEditCSIItems={canEditCSIItems}
        />
      </TabsContent>

      {/* Submittals - full functional section (mirrors Electrical) */}
      {csiItem && (
        <TabsContent value="comm-submittals" data-testid="content-comm-submittals">
          <CsiItemSubmittalsSection
            projectId={projectId}
            csiItem={csiItem}
            canEditCSIItems={canEditCSIItems}
            canManageProject={canManageProject}
            dataTypes={COMMUNICATION_DATA_TYPES}
            testIdPrefix="comm"
            moduleLabel="Communication Systems"
          />
        </TabsContent>
      )}

      {/* Structured Cabling - functional sub-module layout (hybrid nav) */}
      <TabsContent value="comm-structured-cabling" data-testid="content-comm-structured-cabling">
        <StructuredCablingSection
          projectId={projectId}
          csiItem={csiItem}
          canEditCSIItems={canEditCSIItems}
          canManageProject={canManageProject}
        />
      </TabsContent>

      {/* Placeholder Tabs (sub-tabs still to be built) */}
      {TABS.filter(t => t.value !== 'comm-general' && t.value !== 'comm-submittals' && t.value !== 'comm-structured-cabling').map((tab) => (
        <TabsContent key={tab.value} value={tab.value} data-testid={`content-${tab.value}`}>
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <tab.icon className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">{tab.label}</h3>
            <p className="text-sm text-slate-500">Content for {tab.label} coming soon...</p>
          </div>
        </TabsContent>
      ))}

      {/* Share Tab - matching Electrical / Division 8 pattern */}
      {csiItem && (
        <TabsContent value="comm-share" data-testid="content-comm-share">
          <ShareSection
            asContent
            tabValue=""
            inputIdPrefix="comm-share"
            csiItem={csiItem}
            project={project}
            user={user}
            shareCompanySearch={shareCompanySearch}
            showCompanySuggestions={showCompanySuggestions}
            companySuggestions={companySuggestions}
            highlightedIndex={highlightedIndex}
            searchingCompanies={searchingCompanies}
            handleSearchCompanies={handleSearchCompanies}
            handleCompanySearchKeyDown={handleCompanySearchKeyDown}
            setShowCompanySuggestions={setShowCompanySuggestions}
            setHighlightedIndex={setHighlightedIndex}
            handleSelectCompany={handleSelectCompany}
            handleUnshareCSI={handleUnshareCSI}
          />
        </TabsContent>
      )}
    </Tabs>
  );
};
